/**
 * @module core/auth/auth-validators
 * @description Authentication validation functions including user authorization,
 * email domain validation, and origin validation
 */

import { validateOrigin, validateEmailDomain } from '../../utils/validators.js';
import { getCurrentUser } from './auth-manager.js';
import { logger } from '../../utils/logger.js';

/**
 * Required domain for user authentication
 * @constant {string}
 */
export const REQUIRED_DOMAIN = 'shuttersup.co.uk';

/**
 * Check if current user is authorized to delete files using Firebase Custom Claims
 *
 * This function reads the user's custom claims from their Firebase ID token.
 * Claims are set server-side via the SetUserRole Azure Function and are
 * cryptographically signed - they cannot be modified by the client.
 *
 * @returns {Promise<boolean>} True if user is authorized to delete files
 *
 * @example
 * if (await canDeleteFiles()) {
 *   // Show delete button
 * }
 *
 * @throws {Error} If user is not authenticated or token retrieval fails
 */
export async function canDeleteFiles() {
    try {
        const user = getCurrentUser();
        if (!user) {
            logger.warn('⚠️ canDeleteFiles: No user authenticated');
            return false;
        }

        // Get the ID token result which includes custom claims
        const idTokenResult = await user.getIdTokenResult();

        // Check if user has the canDelete custom claim
        const canDelete = idTokenResult.claims.canDelete === true;

        if (canDelete) {
            logger.info('✅ User authorized to delete files (custom claims)');
        } else {
            logger.info('ℹ️ User not authorized to delete files');
        }

        return canDelete;
    } catch (error) {
        logger.error('❌ Error checking delete permissions:', error);
        return false;
    }
}

/**
 * Check if current user has admin role
 *
 * @returns {Promise<boolean>} True if user has admin role
 */
export async function isAdmin() {
    try {
        const user = getCurrentUser();
        if (!user) {
            return false;
        }

        const idTokenResult = await user.getIdTokenResult();
        return idTokenResult.claims.role === 'admin';
    } catch (error) {
        logger.error('❌ Error checking admin role:', error);
        return false;
    }
}

/**
 * Get all custom claims for the current user
 * Useful for debugging and checking what permissions a user has
 *
 * @returns {Promise<Object>} Custom claims object
 */
export async function getUserClaims() {
    try {
        const user = getCurrentUser();
        if (!user) {
            return {};
        }

        const idTokenResult = await user.getIdTokenResult();
        return idTokenResult.claims;
    } catch (error) {
        logger.error('❌ Error getting user claims:', error);
        return {};
    }
}

/**
 * Validate that user email belongs to the required domain
 *
 * @param {string} email - Email address to validate
 * @returns {boolean} True if email belongs to shuttersup.co.uk domain
 *
 * @example
 * if (!isValidUserDomain(user.email)) {
 *   alert('Access restricted to shuttersup.co.uk users only.');
 * }
 */
export function isValidUserDomain(email) {
    return validateEmailDomain(email, REQUIRED_DOMAIN);
}

/**
 * Get the domain from an email address
 *
 * @param {string} email - Email address
 * @returns {string} Domain portion of the email
 *
 * @example
 * getEmailDomain('user@shuttersup.co.uk'); // 'shuttersup.co.uk'
 */
export function getEmailDomain(email) {
    if (!email || typeof email !== 'string') {
        return '';
    }
    return email.substring(email.lastIndexOf('@') + 1);
}

/**
 * Validate application origin (exported from utils/validators)
 * Re-exported for convenience in auth context
 */
export { validateOrigin };

/**
 * Get list of authorized delete users (DEPRECATED)
 *
 * ⚠️ DEPRECATED: This function is deprecated and returns an empty array.
 * Authorization is now managed via Firebase Custom Claims set server-side.
 * Use canDeleteFiles() to check if the current user can delete files.
 *
 * @deprecated Use canDeleteFiles() instead
 * @returns {string[]} Empty array (authorization now via custom claims)
 */
export function getAuthorizedDeleteUsers() {
    logger.warn('⚠️ getAuthorizedDeleteUsers() is deprecated. Authorization now managed via Firebase Custom Claims.');
    return [];
}
